import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { redirectToCheckout } from "./stripe";
import Auth from "./Auth";

// ─── HIPAA Checklist Data ───────────────────────────────────────────
const CATEGORIES = [
  {
    id: "admin", label: "Administrative Safeguards", icon: "🏛️",
    items: [
      { id: "a1", text: "Assigned a HIPAA Security Officer", info: "Designate a responsible individual for HIPAA security policies." },
      { id: "a2", text: "Workforce security training completed", info: "All staff must receive HIPAA training upon hire and annually." },
      { id: "a3", text: "Written HIPAA policies & procedures in place", info: "Document all policies covering PHI access, use, and disclosure." },
      { id: "a4", text: "Business Associate Agreements (BAAs) signed", info: "BAAs required with all vendors who handle PHI on your behalf." },
      { id: "a5", text: "Sanction policy for policy violations documented", info: "Define consequences for employees who violate HIPAA policies." },
      { id: "a6", text: "Contingency plan for emergencies documented", info: "Plan for data backup, disaster recovery, and emergency access." },
      { id: "a7", text: "Access authorization procedures established", info: "Define who can access PHI and under what circumstances." },
    ],
  },
  {
    id: "physical", label: "Physical Safeguards", icon: "🔒",
    items: [
      { id: "p1", text: "Facility access controls in place", info: "Restrict physical access to systems containing PHI." },
      { id: "p2", text: "Workstation use policies defined", info: "Specify proper use and location of workstations accessing PHI." },
      { id: "p3", text: "Device & media controls documented", info: "Policies for disposal and re-use of electronic media with PHI." },
      { id: "p4", text: "Screen privacy filters on public-facing workstations", info: "Prevent unauthorized viewing of PHI on screens." },
      { id: "p5", text: "Visitor access log maintained", info: "Track all visitors to areas where PHI is accessible." },
      { id: "p6", text: "Secure disposal of paper PHI (shredding policy)", info: "Paper records with PHI must be securely shredded." },
    ],
  },
  {
    id: "technical", label: "Technical Safeguards", icon: "💻",
    items: [
      { id: "t1", text: "Unique user IDs for all system users", info: "Each user must have a unique login — no shared accounts." },
      { id: "t2", text: "Automatic logoff on idle workstations enabled", info: "Sessions should auto-lock after a period of inactivity." },
      { id: "t3", text: "Encryption of PHI at rest and in transit", info: "Use AES-256 for storage and TLS 1.2+ for transmission." },
      { id: "t4", text: "Audit logs & access monitoring active", info: "Log all access to systems containing PHI for review." },
      { id: "t5", text: "Multi-factor authentication (MFA) enabled", info: "Require MFA for all accounts accessing PHI systems." },
      { id: "t6", text: "Regular software/security patch updates applied", info: "Keep all systems updated to protect against known vulnerabilities." },
      { id: "t7", text: "Antivirus & firewall protection active", info: "Deploy and maintain endpoint security on all devices." },
    ],
  },
  {
    id: "breach", label: "Breach Notification & Risk", icon: "🚨",
    items: [
      { id: "b1", text: "Breach notification procedures documented", info: "Define steps to notify HHS and patients within 60 days of a breach." },
      { id: "b2", text: "Annual risk analysis conducted", info: "Identify and assess risks to PHI confidentiality, integrity, availability." },
      { id: "b3", text: "Risk management plan in place", info: "Document steps to reduce identified risks to a reasonable level." },
      { id: "b4", text: "Incident response plan tested", info: "Simulate a breach scenario and test your response procedures." },
      { id: "b5", text: "Patient rights procedures established", info: "Processes for patients to access, amend, and restrict their PHI." },
    ],
  },
];

const ALL_ITEMS = CATEGORIES.flatMap(c => c.items);
const TOTAL = ALL_ITEMS.length;
const FREE_LIMIT = 10;

export default function App() {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [checked, setChecked] = useState({});
  const [activeTab, setActiveTab] = useState("checklist");
  const [tooltip, setTooltip] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const plan = profile?.plan || "free";
  const limit = plan === "pro" ? TOTAL : FREE_LIMIT;
  const clinicName = profile?.clinic_name || "My Clinic";
  const completedCount = Object.values(checked).filter(Boolean).length;
  const score = Math.round((completedCount / TOTAL) * 100);
  const riskLevel = score >= 80
    ? { label: "Low Risk", color: "#10b981", bg: "#d1fae5" }
    : score >= 50
    ? { label: "Medium Risk", color: "#f59e0b", bg: "#fef3c7" }
    : { label: "High Risk", color: "#ef4444", bg: "#fee2e2" };

  // ─── Auth listener ───────────────────────────────────────────────
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else { setProfile(null); setChecked({}); setLoading(false); }
    });
    return () => subscription.unsubscribe();
  }, []);

  // ─── Fetch profile + progress ────────────────────────────────────
  const fetchProfile = async (userId) => {
    setLoading(true);
    try {
      const [{ data: prof }, { data: progress }] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", userId).single(),
        supabase.from("checklist_progress").select("*").eq("user_id", userId),
      ]);
      if (prof) setProfile(prof);
      if (progress) {
        const map = {};
        progress.forEach(p => { map[p.item_id] = p.completed; });
        setChecked(map);
      }
    } catch (err) {
      console.error("Error fetching profile:", err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Toggle checklist item ───────────────────────────────────────
  const toggle = async (itemId, idx) => {
    if (idx >= limit && !checked[itemId]) {
      setShowUpgrade(true);
      return;
    }
    const newVal = !checked[itemId];
    setChecked(p => ({ ...p, [itemId]: newVal }));
    setSaving(true);
    try {
      await supabase.from("checklist_progress").upsert({
        user_id: session.user.id,
        item_id: itemId,
        completed: newVal,
        completed_at: newVal ? new Date().toISOString() : null,
      }, { onConflict: "user_id,item_id" });

      // Audit log
      const item = ALL_ITEMS.find(i => i.id === itemId);
      await supabase.from("audit_logs").insert({
        user_id: session.user.id,
        action: newVal ? "item_checked" : "item_unchecked",
        item_id: itemId,
        item_text: item?.text,
      });
    } catch (err) {
      console.error("Error saving progress:", err);
      setChecked(p => ({ ...p, [itemId]: !newVal })); // revert on error
    } finally {
      setSaving(false);
    }
  };

  // ─── Stripe checkout ─────────────────────────────────────────────
  const handleUpgrade = async () => {
    try {
      await redirectToCheckout(session.user.email);
    } catch (err) {
      alert("Checkout failed. Please try again.");
    }
  };

  // ─── Sign out ────────────────────────────────────────────────────
  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const globalIdx = (itemId) => ALL_ITEMS.findIndex(i => i.id === itemId);
  const catScore = (cat) => {
    const done = cat.items.filter(i => checked[i.id]).length;
    return { done, total: cat.items.length, pct: Math.round((done / cat.items.length) * 100) };
  };

  // ─── Loading screen ──────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #1e3a5f, #0f766e)", fontFamily: "'Segoe UI', sans-serif" }}>
      <div style={{ textAlign: "center", color: "#fff" }}>
        <div style={{ fontSize: 48 }}>🛡️</div>
        <div style={{ fontWeight: 700, fontSize: 18, marginTop: 12 }}>Loading HIPAA Comply...</div>
      </div>
    </div>
  );

  // ─── Auth screen ─────────────────────────────────────────────────
  if (!session) return <Auth />;

  // ─── Main App ────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", background: "#f8fafc", minHeight: "100vh", color: "#1e293b" }}>

      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #1e3a5f 0%, #0f766e 100%)", color: "#fff", padding: "16px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 24 }}>🛡️</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 18 }}>HIPAA Comply</div>
            <div style={{ fontSize: 11, opacity: 0.8 }}>by Chenations LLC</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {saving && <span style={{ fontSize: 12, opacity: 0.8 }}>💾 Saving...</span>}
          <span style={{ background: plan === "pro" ? "#f59e0b" : "rgba(255,255,255,0.2)", borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 600 }}>
            {plan === "pro" ? "⭐ PRO" : "FREE"}
          </span>
          {plan === "free" && (
            <button onClick={() => setShowUpgrade(true)} style={{ background: "#fff", color: "#0f766e", border: "none", borderRadius: 20, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              Upgrade $10.99/mo
            </button>
          )}
          <button onClick={signOut} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "none", borderRadius: 20, padding: "5px 14px", fontSize: 12, cursor: "pointer" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Clinic Name Bar */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "10px 24px", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 13, color: "#64748b" }}>Clinic:</span>
        <span style={{ fontWeight: 700, fontSize: 14 }}>{clinicName}</span>
        <span style={{ fontSize: 12, color: "#94a3b8" }}>— {session.user.email}</span>
      </div>

      {/* Tabs */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", display: "flex", padding: "0 24px" }}>
        {[["checklist", "✅ Checklist"], ["dashboard", "📊 Dashboard"], ["report", "📄 Report"]].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            padding: "12px 18px", border: "none", background: "none", cursor: "pointer",
            fontWeight: activeTab === id ? 700 : 400,
            color: activeTab === id ? "#0f766e" : "#64748b",
            borderBottom: activeTab === id ? "3px solid #0f766e" : "3px solid transparent",
            fontSize: 13,
          }}>{label}</button>
        ))}
      </div>

      <div style={{ maxWidth: 860, margin: "0 auto", padding: "20px 16px" }}>

        {/* Score Banner */}
        <div style={{ background: "#fff", borderRadius: 12, padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", flexWrap: "wrap" }}>
          <div style={{ position: "relative", width: 72, height: 72, flexShrink: 0 }}>
            <svg viewBox="0 0 36 36" style={{ width: 72, height: 72, transform: "rotate(-90deg)" }}>
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3.5" />
              <circle cx="18" cy="18" r="15.9" fill="none" stroke={riskLevel.color} strokeWidth="3.5" strokeDasharray={`${score} 100`} strokeLinecap="round" />
            </svg>
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: riskLevel.color }}>{score}%</div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 17 }}>{clinicName}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
              <span style={{ background: riskLevel.bg, color: riskLevel.color, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{riskLevel.label}</span>
              <span style={{ color: "#64748b", fontSize: 13 }}>{completedCount} of {TOTAL} items complete</span>
            </div>
            <div style={{ background: "#e2e8f0", borderRadius: 8, height: 6, marginTop: 10 }}>
              <div style={{ background: riskLevel.color, height: 6, borderRadius: 8, width: `${score}%`, transition: "width 0.4s" }} />
            </div>
          </div>
          {plan === "free" && (
            <div style={{ background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#92400e", maxWidth: 160 }}>
              ⚠️ Free: {FREE_LIMIT} of {TOTAL} items unlocked.{" "}
              <span onClick={() => setShowUpgrade(true)} style={{ fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}>Upgrade</span>
            </div>
          )}
        </div>

        {/* CHECKLIST TAB */}
        {activeTab === "checklist" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {CATEGORIES.map(cat => {
              const cs = catScore(cat);
              return (
                <div key={cat.id} style={{ background: "#fff", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
                  <div style={{ padding: "14px 18px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{cat.icon} {cat.label}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ background: "#e2e8f0", borderRadius: 8, height: 6, width: 80 }}>
                        <div style={{ background: cs.pct === 100 ? "#10b981" : "#0f766e", height: 6, borderRadius: 8, width: `${cs.pct}%`, transition: "width 0.3s" }} />
                      </div>
                      <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{cs.done}/{cs.total}</span>
                    </div>
                  </div>
                  {cat.items.map((item, i) => {
                    const gIdx = globalIdx(item.id);
                    const locked = gIdx >= limit && plan === "free";
                    const done = !!checked[item.id];
                    return (
                      <div key={item.id} onClick={() => toggle(item.id, gIdx)}
                        style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 18px", borderBottom: i < cat.items.length - 1 ? "1px solid #f1f5f9" : "none", cursor: locked ? "not-allowed" : "pointer", opacity: locked ? 0.5 : 1, background: done ? "#f0fdf4" : "#fff", transition: "background 0.2s" }}>
                        <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${done ? "#10b981" : "#cbd5e1"}`, background: done ? "#10b981" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {done && <span style={{ color: "#fff", fontSize: 13, fontWeight: 800 }}>✓</span>}
                          {locked && <span style={{ fontSize: 10 }}>🔒</span>}
                        </div>
                        <span style={{ flex: 1, fontSize: 14, color: done ? "#065f46" : "#334155", fontWeight: done ? 600 : 400 }}>{item.text}</span>
                        <span onMouseEnter={e => { e.stopPropagation(); setTooltip(item.id); }}
                          onMouseLeave={() => setTooltip(null)}
                          onClick={e => e.stopPropagation()}
                          style={{ color: "#94a3b8", fontSize: 16, cursor: "help", position: "relative" }}>
                          ℹ️
                          {tooltip === item.id && (
                            <div style={{ position: "absolute", right: 0, top: 24, background: "#1e293b", color: "#fff", borderRadius: 8, padding: "8px 12px", fontSize: 12, width: 220, zIndex: 10, lineHeight: 1.5, whiteSpace: "normal" }}>
                              {item.info}
                            </div>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}

        {/* DASHBOARD TAB */}
        {activeTab === "dashboard" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              {[
                { label: "Overall Score", value: `${score}%`, icon: "📊", color: riskLevel.color },
                { label: "Completed", value: completedCount, icon: "✅", color: "#10b981" },
                { label: "Remaining", value: TOTAL - completedCount, icon: "⏳", color: "#f59e0b" },
                { label: "Risk Level", value: riskLevel.label, icon: "🚨", color: riskLevel.color },
              ].map(s => (
                <div key={s.label} style={{ background: "#fff", borderRadius: 12, padding: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", textAlign: "center" }}>
                  <div style={{ fontSize: 28 }}>{s.icon}</div>
                  <div style={{ fontWeight: 800, fontSize: 22, color: s.color, marginTop: 4 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 12, padding: 18, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
              <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 15 }}>Category Breakdown</div>
              {CATEGORIES.map(cat => {
                const cs = catScore(cat);
                const color = cs.pct === 100 ? "#10b981" : cs.pct >= 50 ? "#f59e0b" : "#ef4444";
                return (
                  <div key={cat.id} style={{ marginBottom: 14 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5, fontSize: 13 }}>
                      <span>{cat.icon} {cat.label}</span>
                      <span style={{ fontWeight: 700, color }}>{cs.done}/{cs.total}</span>
                    </div>
                    <div style={{ background: "#e2e8f0", borderRadius: 8, height: 8 }}>
                      <div style={{ background: color, height: 8, borderRadius: 8, width: `${cs.pct}%`, transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* REPORT TAB */}
        {activeTab === "report" && (
          <div style={{ background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 20 }}>HIPAA Compliance Report</div>
                <div style={{ color: "#64748b", fontSize: 13 }}>Generated: {new Date().toLocaleDateString()}</div>
              </div>
              <button onClick={() => window.print()} style={{ background: "#0f766e", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
                🖨️ Print Report
              </button>
            </div>
            <div style={{ borderTop: "3px solid #0f766e", paddingTop: 16 }}>
              <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
                {[
                  { label: "RISK LEVEL", value: riskLevel.label, bg: riskLevel.bg, color: riskLevel.color },
                  { label: "COMPLIANCE SCORE", value: `${score}%`, bg: "#f0fdf4", color: "#10b981" },
                  { label: "ITEMS COMPLETED", value: `${completedCount}/${TOTAL}`, bg: "#f8fafc", color: "#1e293b" },
                ].map(s => (
                  <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: "12px 20px", flex: 1 }}>
                    <div style={{ fontSize: 12, color: s.color, fontWeight: 600 }}>{s.label}</div>
                    <div style={{ fontWeight: 800, fontSize: 22, color: s.color }}>{s.value}</div>
                  </div>
                ))}
              </div>
              {CATEGORIES.map(cat => {
                const cs = catScore(cat);
                return (
                  <div key={cat.id} style={{ marginBottom: 18 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                      <span>{cat.icon} {cat.label}</span>
                      <span style={{ color: cs.pct === 100 ? "#10b981" : "#f59e0b" }}>{cs.done}/{cs.total} ({cs.pct}%)</span>
                    </div>
                    {cat.items.map(item => (
                      <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, padding: "4px 0", color: checked[item.id] ? "#065f46" : "#7f1d1d" }}>
                        <span>{checked[item.id] ? "✅" : "❌"}</span> {item.text}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgrade && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: "28px 24px", maxWidth: 400, width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 36 }}>⭐</div>
              <div style={{ fontWeight: 800, fontSize: 20, marginTop: 6 }}>Upgrade to Pro</div>
              <div style={{ color: "#64748b", fontSize: 14, marginTop: 4 }}>Unlock the full HIPAA compliance suite</div>
            </div>
            <div style={{ background: "#f0fdf4", border: "2px solid #0f766e", borderRadius: 12, padding: 16, marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>Pro Plan</div>
              <div style={{ fontWeight: 800, fontSize: 28, color: "#0f766e", margin: "6px 0" }}>$10.99<span style={{ fontSize: 14, fontWeight: 400 }}>/mo</span></div>
              {["All 25 checklist items", "Full compliance report", "AI risk recommendations", "Audit log export", "Email reminders", "Cancel anytime"].map(f => (
                <div key={f} style={{ fontSize: 13, color: "#475569", marginTop: 5 }}>✓ {f}</div>
              ))}
            </div>
            <button onClick={handleUpgrade} style={{ width: "100%", background: "linear-gradient(135deg, #0f766e, #1e3a5f)", color: "#fff", border: "none", borderRadius: 10, padding: 13, fontWeight: 700, fontSize: 15, cursor: "pointer", marginBottom: 10 }}>
              Start Pro — $10.99/mo →
            </button>
            <button onClick={() => setShowUpgrade(false)} style={{ width: "100%", background: "none", border: "none", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}>
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
}